import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, UserCheck, BarChart3, ChevronDown, UserCog } from 'lucide-react';
import { Event, EventFormData, EventCategory, getEffectiveEventStatus } from '../../types/event';
import { useAppSelector } from '../../store/hooks';
import eventAPI from '../../services/eventAPI';
import { guestAPI } from '../../services/guestAPI';
import categoryAPI from '../../services/categoryAPI';
import { webSocketService } from '../../services/websocketService';
import Button from '../../components/UI/Button';
import Toast from '../../components/UI/Toast';
import { slugToTitle, generateEventViewUrl } from '../../utils/urlUtils';
import { GuestManagement } from '../../components/Events/GuestManagement';
import EnhancedOverviewDashboard from '../../components/Events/EnhancedOverviewDashboard';
import { EventActionBar } from '../../components/Events/EventActionBar';
import EnhancedEventDetailsForm from '../../components/Events/EnhancedEventDetailsForm';
import { RescheduleConfirmationDialog } from '../../components/Events/RescheduleConfirmationDialog';
import SeriesEventsSection from '../../components/Events/SeriesEventsSection';
import EventPageHeader from '../../components/Events/EventPageHeader';
import EventAttendanceManagement from '../../components/Events/EventAttendanceManagement';
import EventDestructiveActions from '../../components/Events/EventDestructiveActions';
import UsherManagement from '../../components/Events/UsherManagement';
import { canEditEvent, hasEventEnded } from '../../utils/eventUtils';
import EventRejectionBanner from '../../components/Events/EventRejectionBanner';
import { logError } from '../../utils/errorLogger';
import { useAutoDismiss } from '../../hooks/useAutoDismiss';
import ConfirmationModal from '../../components/UI/ConfirmationModal';
import { BillingActionPreview, CostBreakdown } from '../../components/Billing';
import { billingService, PricingConfiguration, HostBalance } from '../../services/billingService';

const OPTIONAL_HTTP_URL = /^https?:\/\/.+/;

function optionalHttpUrlError(value: string | undefined): string | undefined {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return undefined;
  }
  if (!OPTIONAL_HTTP_URL.test(trimmed)) {
    return 'Please enter a valid URL starting with http:// or https://';
  }
  return undefined;
}

function eventToEditData(eventData: Event): Partial<EventFormData> {
  return {
    title: eventData.title,
    event_date: eventData.event_date,
    event_time: eventData.event_time,
    location: eventData.location,
    description: eventData.description || '',
    category_id: eventData.category?.id,
    guest_limit: eventData.guest_limit,
    editable_until: eventData.editable_until,
    editing_window_hours: eventData.editing_window_hours,
    message_template: eventData.message_template || '',
    image_url: eventData.image_url || undefined,
    image_id: eventData.image_id || undefined,
    image_shape: eventData.image_shape || undefined,
    show_total_guests_invited: eventData.show_total_guests_invited ?? false,
    location_link: eventData.location_link || '',
    website_link: eventData.website_link || '',
    dress_code: eventData.dress_code || '',
  };
}

const EventViewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ eventId?: string; slug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAppSelector((state) => state.auth);
  const { theme } = useAppSelector((state) => state.ui);
  
  // State
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Auto-dismiss success messages
  useAutoDismiss(success, () => setSuccess(null), 3000);
  
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUpdatedEvent, setHasUpdatedEvent] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'guests' | 'attendance'>('details');
  const [guestStats, setGuestStats] = useState<any>(null);
  const [categories, setCategories] = useState<EventCategory[]>([]);
  
  // Form data for editing
  const [editData, setEditData] = useState<Partial<EventFormData>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  
  // Reschedule confirmation dialog state
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [eventGuests, setEventGuests] = useState<any[]>([]);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [showUsherManagement, setShowUsherManagement] = useState(false);
  const [showEnableUsherServiceConfirm, setShowEnableUsherServiceConfirm] = useState(false);
  const [usherServicePricing, setUsherServicePricing] = useState<PricingConfiguration | null>(null);
  const [usherServiceBalance, setUsherServiceBalance] = useState<HostBalance | null>(null);
  const [usherServiceLoading, setUsherServiceLoading] = useState(false);
  const [enablingUsherService, setEnablingUsherService] = useState(false);
  
  // Dropdown state for mobile tab navigation
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Sync tab state with URL parameters
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl === 'guests' || tabFromUrl === 'details' || tabFromUrl === 'attendance') {
      setActiveTab(tabFromUrl as 'details' | 'guests' | 'attendance');
    }
  }, [searchParams]);

  // Function to update tab and URL
  const updateActiveTab = (tab: 'details' | 'guests' | 'attendance') => {
    setActiveTab(tab);
    setIsDropdownOpen(false); // Close dropdown when tab changes
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', tab);
      return newParams;
    });
  };

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  // Function to refresh guest stats
  const refreshGuestStats = useCallback(async (eventId?: string) => {
    const idToUse = eventId || event?.id;
    if (!idToUse) return;
    
    try {
      // Disable cache to ensure we get fresh data, especially after guest responses
      const guests = await guestAPI.getGuests(idToUse, { page: 1, page_size: 1000 });
      
      // Calculate total companions from guest data
      let totalCompanions = 0;
      guests.results.forEach(guest => {
        // Sum companion_count from all guests (includes both detailed and empty companions)
        totalCompanions += guest.companion_count || 0;
      });
      
      // Use the summary data from the API response if available
      if (guests.summary) {
        setGuestStats({
          total_invited: guests.summary.total_invited,
          total_attendees: guests.summary.total_attendees,
          total_companions: totalCompanions,
          response_rate: guests.summary.response_rate,
          status_breakdown: guests.summary.status_breakdown
        });
      } else {
        // Fallback to manual calculation if summary is not available
        const statusBreakdown = {
          pending: 0,
          accepted: 0,
          rejected: 0,
          ignored: 0
        };

        let totalAttendees = 0;
        guests.results.forEach(guest => {
          if (guest.status in statusBreakdown) {
            statusBreakdown[guest.status as keyof typeof statusBreakdown]++;
          }
          // Sum total_attendees from each guest (includes accepted companions - detailed + empty)
          if (guest.status === 'accepted') {
            totalAttendees += guest.total_attendees || 1; // Fallback to 1 if total_attendees not available
          }
        });

        const totalInvited = guests.count || 0;
        const totalResponded = statusBreakdown.accepted + statusBreakdown.rejected + statusBreakdown.ignored;
        const responseRate = totalInvited > 0 ? (totalResponded / totalInvited) * 100 : 0;

        setGuestStats({
          total_invited: totalInvited,
          total_attendees: totalAttendees,
          total_companions: totalCompanions,
          response_rate: responseRate,
          status_breakdown: statusBreakdown
        });
      }
    } catch (guestError) {
      logError('Error refreshing guest stats', guestError);
    }
  }, [event?.id]);

  // Get slug from route parameters (no event ID in URL for security)
  const eventSlug = params.slug;
  
  // Check if slug is actually a UUID (36 chars with dashes) - indicates old format with ID
  // This handles backward compatibility with old URLs that had event IDs
  const isSlugUUID = eventSlug && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(eventSlug);
  
  // If slug is a UUID, we can't use slug-based lookup (old URL format)
  // For new format, slug should always be a valid event title slug
  const actualSlug = eventSlug && !isSlugUUID ? eventSlug : null;
  
  const eventTitle = actualSlug ? slugToTitle(actualSlug) : null;


  // Scroll to top when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, actualSlug]);

  // Event view page initialization
  useEffect(() => {
    // Page initialized
  }, [location.pathname, actualSlug, eventTitle, event]);

  // Fetch event data
  useEffect(() => {
    if (!actualSlug) {
      logError('No event slug found in URL');
      setError('Event identifier not found in URL');
      setLoading(false);
      return;
    }

    // If we've already updated the event via save, don't fetch again
    if (hasUpdatedEvent) {
      return;
    }

    const fetchEvent = async () => {
      try {
        setLoading(true);
        
        // Always fetch by slug (no event ID in URL for security)
        const eventData = await eventAPI.getEventBySlug(actualSlug);
        setEvent(eventData);
        
        // Fetch guest stats
        await refreshGuestStats(eventData.id);
        
        // Load categories for editing
        try {
          const categoriesData = await categoryAPI.getCategories();
          setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        } catch (err) {
          logError('Error loading categories', err);
          setCategories([]);
        }
        
        setEditData(eventToEditData(eventData));
      } catch (err: any) {
        logError('Error fetching event', err);
        setError(err.response?.data?.message || 'Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [actualSlug, hasUpdatedEvent, refreshGuestStats]);

  // Real-time WebSocket updates for guest statistics
  useEffect(() => {
    if (!event?.id) return;
    
    const handleGuestResponse = (data: any) => {
      // Only update if this is for the current event
      if (data.event_id && data.event_id.toString() === event.id.toString()) {
        refreshGuestStats();
      }
    };

    const handleStatisticsUpdate = (data: any) => {
      // Only update if this is for the current event
      if (data.event_id && data.event_id.toString() === event.id.toString()) {
        // Update guest stats directly from the WebSocket data
        setGuestStats((prevStats: any) => {
          if (!prevStats) return prevStats;
          
          const newStats = {
            ...prevStats,
            total_invited: data.total_invites ?? prevStats.total_invited,
            status_breakdown: {
              pending: data.pending_count ?? prevStats.status_breakdown.pending,
              accepted: data.accepted_count ?? prevStats.status_breakdown.accepted,
              rejected: data.rejected_count ?? prevStats.status_breakdown.rejected,
              ignored: data.ignored_count ?? prevStats.status_breakdown.ignored
            }
          };
          
          // Recalculate response rate
          const totalResponses = newStats.status_breakdown.accepted + newStats.status_breakdown.rejected + newStats.status_breakdown.ignored;
          newStats.response_rate = newStats.total_invited > 0 ? (totalResponses / newStats.total_invited) * 100 : 0;
          
          return newStats;
        });
      }
    };

    // Subscribe to real-time updates
    const unsubscribeGuestResponse = webSocketService.on('guest_response', handleGuestResponse);
    const unsubscribeStatistics = webSocketService.on('event_statistics_update', handleStatisticsUpdate);

    return () => {
      unsubscribeGuestResponse();
      unsubscribeStatistics();
    };
  }, [event?.id, refreshGuestStats]); // Only re-run when event ID changes

  // Prevent entering edit mode when editing is not allowed
  useEffect(() => {
    if (event && isEditing) {
      if (!canEditEvent(event)) {
        setIsEditing(false);
        let errorMessage = '';
        if (!event.can_edit_event) {
          if (event.editing_window_hours) {
            errorMessage = `Event editing window has closed. Editing is only allowed ${event.editing_window_hours} hours before the event starts.`;
          } else {
            errorMessage = 'Event editing window has closed.';
          }
        } else if (hasEventEnded(event)) {
          errorMessage = 'Event has ended - editing is not allowed.';
        } else {
          errorMessage = 'Event editing is not allowed at this time.';
        }
        setError(errorMessage);
      }
    }
  }, [event, isEditing]);

  // Handle input changes
  const handleInputChange = (field: keyof EventFormData, value: any) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Handle save
  const handleSave = async () => {
    if (!event) return;

    // Double-check editing is allowed before saving
    if (!canEditEvent(event)) {
      let errorMessage = '';
      if (!event.can_edit_event) {
        if (event.editing_window_hours) {
          errorMessage = `Event editing window has closed. Editing is only allowed ${event.editing_window_hours} hours before the event starts.`;
        } else {
          errorMessage = 'Event editing window has closed.';
        }
      } else if (hasEventEnded(event)) {
        errorMessage = 'Event has ended - editing is not allowed.';
      } else {
        errorMessage = 'Event editing is not allowed at this time.';
      }
      setError(errorMessage);
      setIsEditing(false);
      return;
    }

    const nextFieldErrors: Record<string, string> = {};
    const locationLink = (editData.location_link ?? '').trim();
    if (!locationLink) {
      nextFieldErrors.location_link = 'Location link is required';
    } else {
      const locationLinkError = optionalHttpUrlError(locationLink);
      if (locationLinkError) {
        nextFieldErrors.location_link = locationLinkError;
      }
    }
    const websiteLinkError = optionalHttpUrlError(editData.website_link);
    if (websiteLinkError) {
      nextFieldErrors.website_link = websiteLinkError;
    }
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setFieldErrors({});

      const payload: Partial<EventFormData> = {
        ...editData,
        location_link: (editData.location_link || '').trim(),
        website_link: (editData.website_link || '').trim(),
        dress_code: (editData.dress_code || '').trim(),
      };

      const updatedEvent = await eventAPI.updateEvent(event.id, payload);
      setEvent(updatedEvent);
      
      setIsEditing(false);
      
      setHasUpdatedEvent(true); // Mark that we've updated the event
      
      // Check if event was rescheduled by comparing date/time changes
      const originalDate = event.event_date ? new Date(event.event_date).toISOString().split('T')[0] : null;
      const newDate = editData.event_date ? new Date(editData.event_date).toISOString().split('T')[0] : null;
      const originalTime = event.event_time ? event.event_time.substring(0, 5) : null; // HH:MM format
      const newTime = editData.event_time ? editData.event_time.substring(0, 5) : null; // HH:MM format
      
      const wasRescheduled = (
        (newDate && newDate !== originalDate) ||
        (newTime && newTime !== originalTime)
      );
      
      if (wasRescheduled) {
        // Fetch guests for the confirmation dialog
        try {
          // Fetch all guests across all pages
          let allGuests: any[] = [];
          let currentPage = 1;
          let hasMore = true;
          
          while (hasMore) {
            const guestsResponse = await guestAPI.getGuests(event.id, { page: currentPage, page_size: 100 });
            allGuests = [...allGuests, ...guestsResponse.results];
            
            hasMore = guestsResponse.next != null;
            currentPage++;
            
            // Safety check
            if (currentPage > 20) break;
          }
          
          const mappedGuests = allGuests.map((guest: any) => ({
            id: guest.guest.id,
            first_name: guest.guest.first_name,
            last_name: guest.guest.last_name,
            phone: guest.guest.phone,
            email: guest.guest.email,
            status: guest.status
          }));
          
          setEventGuests(mappedGuests);
          setShowRescheduleDialog(true);
        } catch (err) {
          logError('Failed to fetch guests for reschedule dialog', err);
          setSuccess('Event rescheduled successfully!');
        }
      } else {
        setSuccess('Event updated successfully!');
      }
      
      // If event was rescheduled, refresh guest stats to show reset statuses
      if (wasRescheduled) {
        refreshGuestStats();
      }
      
      // Check if title or date changed (which would change the slug)
      const titleChanged = editData.title && editData.title !== event.title;
      const dateChanged = newDate && newDate !== originalDate;
      
      // If title or date changed, navigate to the new URL with updated slug
      if (titleChanged || dateChanged) {
        const newUrl = generateEventViewUrl(
          updatedEvent.id,
          updatedEvent.title,
          updatedEvent.event_date,
          user?.role,
          updatedEvent.slug
        );
        // Use replace: true to update URL without adding to history
        navigate(newUrl, { replace: true });
      }
      
    } catch (err: any) {
      logError('Event update failed', err);
      setError(err.response?.data?.message || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (!event) return;
    
    setEditData(eventToEditData(event));
    setFieldErrors({});
    
    setIsEditing(false);
    setError(null);
    setSuccess(null);
    setHasUpdatedEvent(false); // Reset update flag
  };

  // Handle cancel event
  const handleCancelEvent = async (reason: string) => {
    if (!event) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const updatedEvent = await eventAPI.cancelEvent(event.id, { reason });
      setEvent(updatedEvent);
      setSuccess('Event cancelled successfully');
      
    } catch (err: any) {
      logError('Event cancellation failed', err);
      setError(err.response?.data?.message || 'Failed to cancel event');
    } finally {
      setSaving(false);
    }
  };

  // Handle archive event
  const handleArchiveEvent = async () => {
    if (!event) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const updatedEvent = await eventAPI.archiveEvent(event.id, {});
      setEvent(updatedEvent);
      setSuccess('Event archived successfully');
      
    } catch (err: any) {
      logError('Event archiving failed', err);
      setError(err.response?.data?.message || 'Failed to archive event');
    } finally {
      setSaving(false);
    }
  };

  // Handle unarchive event
  const handleUnarchiveEvent = async () => {
    if (!event) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const updatedEvent = await eventAPI.reactivateEvent(event.id, {});
      setEvent(updatedEvent);
      setSuccess('Event unarchived successfully');
      
    } catch (err: any) {
      logError('Event unarchiving failed', err);
      setError(err.response?.data?.message || 'Failed to unarchive event');
    } finally {
      setSaving(false);
    }
  };

  // Fetch billing data when confirmation modal opens
  useEffect(() => {
    if (showEnableUsherServiceConfirm) {
      const fetchBillingData = async () => {
        try {
          setUsherServiceLoading(true);
          const [pricingData, balanceData] = await Promise.all([
            billingService.getCurrentPricing().catch(() => null),
            billingService.getMyBalance().catch(() => null)
          ]);
          setUsherServicePricing(pricingData);
          setUsherServiceBalance(balanceData);
        } catch (error) {
          logError('Failed to fetch billing data', error);
        } finally {
          setUsherServiceLoading(false);
        }
      };
      fetchBillingData();
    }
  }, [showEnableUsherServiceConfirm]);

  // Handle enable usher service
  const handleEnableUsherService = async () => {
    if (!event) return;
    
    try {
      setEnablingUsherService(true);
      setError(null);
      
      const updatedEvent = await eventAPI.enableUsherService(event.id);
      setEvent(updatedEvent);
      setShowEnableUsherServiceConfirm(false);
      setSuccess('Usher service enabled successfully');
      
    } catch (err: any) {
      logError('Failed to enable usher service', err);
      setError(err.response?.data?.error?.message || 'Failed to enable usher service');
    } finally {
      setEnablingUsherService(false);
    }
  };

  // Calculate usher service cost
  const calculateUsherServiceCost = (): CostBreakdown[] => {
    const costs: CostBreakdown[] = [];
    if (usherServicePricing && usherServicePricing.usher_service_fee) {
      const fee = parseFloat(usherServicePricing.usher_service_fee);
      if (fee > 0) {
        costs.push({
          label: 'Usher Service Fee',
          amount: fee,
          quantity: 1,
          unitPrice: fee
        });
      }
    }
    return costs;
  };

  // Handle back navigation
  const handleBack = () => {
    const rolePrefix = user?.role === 'admin' ? '/admin' : `/${user?.role}`;
    navigate(`${rolePrefix}/events`);
  };

  // Quick action handlers
  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'edit':
        setIsEditing(true);
        break;
      case 'guests':
        updateActiveTab('guests');
        break;
      case 'share':
        // TODO: Implement share functionality
        setSuccess('Share functionality coming soon!');
        break;
      case 'analytics':
        // TODO: Implement analytics
        setSuccess('Analytics feature coming soon!');
        break;
      case 'remind':
        // TODO: Implement bulk reminders
        setSuccess('Reminder functionality coming soon!');
        break;
      default:
        // Unknown action
        break;
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${
          theme === 'dark' ? 'border-[#33FF00]' : 'border-mirsal-green'
        }`}></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <h1 className={`text-2xl font-bold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Event Not Found
          </h1>
          <p className={`mb-8 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            The requested event could not be found.
          </p>
          <Button onClick={handleBack}>
            Back to Events
          </Button>
        </div>
      </div>
    );
  }


  // Handle reschedule notification confirmation
  const handleRescheduleNotificationConfirm = async (selectedGuestIds: string[]) => {
    if (!event) return;
    
    try {
      setSendingNotifications(true);
      
      // Call the new API endpoint for selective notifications using eventAPI
      const response = await eventAPI.rescheduleNotifyGuests(event.id, selectedGuestIds);
      
      if (response.success) {
        setSuccess(`Reschedule notifications sent to ${response.notification_results.sent_count} guests!`);
        setShowRescheduleDialog(false);
      } else {
        setError(response.error?.message || 'Failed to send notifications');
      }
    } catch (err: any) {
      logError('Failed to send reschedule notifications', err);
      setError('Failed to send notifications. Please try again.');
    } finally {
      setSendingNotifications(false);
    }
  };

  return (
    <div>
      {/* Success/Error Messages */}
      {success && (
        <div className="fixed top-4 right-4 z-[120] max-w-md">
          <Toast
            type="success"
            message={success}
            onClose={() => setSuccess(null)}
          />
        </div>
      )}
      
      {error && (
        <div className="fixed top-4 right-4 z-[120] max-w-md">
          <Toast
            type="error"
            message={error}
            onClose={() => setError(null)}
          />
        </div>
      )}

      {/* Enhanced Header */}
      <EventPageHeader
        event={event}
        onBack={handleBack}
        guestStats={guestStats}
      />

      {/* Rejection Banner */}
      {getEffectiveEventStatus(event) === 'rejected' && event.rejection_reason && (
        <EventRejectionBanner rejectionReason={event.rejection_reason} />
      )}

      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {/* Content Cards */}
        <div className="grid grid-cols-1 gap-6">
          {/* Modern Tabs Navigation */}
          <div className={`relative rounded-2xl shadow-2xl border backdrop-blur-sm overflow-hidden ${
            theme === 'dark' 
              ? 'bg-black/80 border-gray-800/50' 
              : 'bg-white/90 border-gray-200/50'
          }`}>
            {/* Mobile Dropdown (visible on < 768px) */}
            <div className="md:hidden relative overflow-visible">
              <div className={`border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="relative">
                  <button
                    ref={buttonRef}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`w-full flex items-center justify-center relative py-4 px-4 text-sm font-semibold transition-all duration-200 ${
                      activeTab === 'details'
                        ? theme === 'dark'
                          ? 'bg-gray-800 text-[#33FF00] border-b-2 border-[#33FF00] -mb-px relative shadow-sm'
                          : 'bg-white text-mirsal-green border-b-2 border-mirsal-green -mb-px relative shadow-sm'
                        : activeTab === 'guests'
                        ? theme === 'dark'
                          ? 'bg-gray-800 text-[#33FF00] border-b-2 border-[#33FF00] -mb-px relative shadow-sm'
                          : 'bg-white text-mirsal-green border-b-2 border-mirsal-green -mb-px relative shadow-sm'
                        : activeTab === 'attendance'
                        ? theme === 'dark'
                          ? 'bg-gray-800 text-[#33FF00] border-b-2 border-[#33FF00] -mb-px relative shadow-sm'
                          : 'bg-white text-mirsal-green border-b-2 border-mirsal-green -mb-px relative shadow-sm'
                        : theme === 'dark'
                        ? 'bg-gray-800 text-white hover:bg-gray-700'
                        : 'bg-white text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {activeTab === 'details' && (
                        <Calendar className={`h-4 w-4 ${
                          theme === 'dark' ? 'text-[#33FF00]' : 'text-mirsal-green'
                        }`} />
                      )}
                      {activeTab === 'guests' && (
                        <UserCheck className={`h-4 w-4 ${
                          theme === 'dark' ? 'text-[#33FF00]' : 'text-mirsal-green'
                        }`} />
                      )}
                      {activeTab === 'attendance' && (
                        <BarChart3 className={`h-4 w-4 ${
                          theme === 'dark' ? 'text-[#33FF00]' : 'text-mirsal-green'
                        }`} />
                      )}
                      <span>
                        {activeTab === 'details' && 'Event Details'}
                        {activeTab === 'guests' && 'Guest Management'}
                        {activeTab === 'attendance' && 'Attendance'}
                      </span>
                    </div>
                    <ChevronDown className={`absolute right-4 h-4 w-4 transition-transform duration-200 ${
                      isDropdownOpen ? 'rotate-180' : ''
                    } ${activeTab === 'details' || activeTab === 'guests' || activeTab === 'attendance'
                      ? theme === 'dark' ? 'text-[#33FF00]' : 'text-mirsal-green'
                      : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div
                      ref={dropdownRef}
                      className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden ${
                        theme === 'dark'
                          ? 'bg-gray-800 border border-gray-700'
                          : 'bg-white border border-gray-200'
                      }`}
                      style={{ top: '100%' }}
                    >
                      <div className="py-1">
                        {/* Details Tab */}
                        <button
                          onClick={() => updateActiveTab('details')}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                            activeTab === 'details'
                              ? theme === 'dark'
                                ? 'bg-gray-700 text-[#33FF00]'
                                : 'bg-gray-100 text-mirsal-green'
                              : theme === 'dark'
                                ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <Calendar className={`h-4 w-4 ${
                            activeTab === 'details'
                              ? theme === 'dark' ? 'text-[#33FF00]' : 'text-mirsal-green'
                              : ''
                          }`} />
                          <span>Event Details</span>
                        </button>
                        
                        {/* Guests Tab */}
                        <button
                          onClick={() => {
                            if (event && ['active', 'rescheduled'].includes(getEffectiveEventStatus(event))) {
                              updateActiveTab('guests');
                            }
                          }}
                          disabled={event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                            event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))
                              ? theme === 'dark'
                                ? 'text-gray-500 cursor-not-allowed opacity-50'
                                : 'text-gray-400 cursor-not-allowed opacity-50'
                              : activeTab === 'guests'
                              ? theme === 'dark'
                                ? 'bg-gray-700 text-[#33FF00]'
                                : 'bg-gray-100 text-mirsal-green'
                              : theme === 'dark'
                                ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                          title={
                            event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))
                              ? `Guest management is not available for ${getEffectiveEventStatus(event)} events`
                              : 'Manage event guests'
                          }
                        >
                          <UserCheck className={`h-4 w-4 ${
                            activeTab === 'guests'
                              ? theme === 'dark' ? 'text-[#33FF00]' : 'text-mirsal-green'
                              : ''
                          }`} />
                          <span>Guest Management</span>
                          {event && getEffectiveEventStatus(event) === 'pending' && (
                            <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${
                              theme === 'dark'
                                ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-700/50'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              Approval Required
                            </span>
                          )}
                        </button>
                        
                        {/* Attendance Tab */}
                        <button
                          onClick={() => {
                            if (event && ['active', 'rescheduled'].includes(getEffectiveEventStatus(event))) {
                              updateActiveTab('attendance');
                            }
                          }}
                          disabled={event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                            event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))
                              ? theme === 'dark'
                                ? 'text-gray-500 cursor-not-allowed opacity-50'
                                : 'text-gray-400 cursor-not-allowed opacity-50'
                              : activeTab === 'attendance'
                              ? theme === 'dark'
                                ? 'bg-gray-700 text-[#33FF00]'
                                : 'bg-gray-100 text-mirsal-green'
                              : theme === 'dark'
                                ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                          title={
                            event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))
                              ? `Attendance management is not available for ${getEffectiveEventStatus(event)} events`
                              : 'Manage guest attendance'
                          }
                        >
                          <BarChart3 className={`h-4 w-4 ${
                            activeTab === 'attendance'
                              ? theme === 'dark' ? 'text-[#33FF00]' : 'text-mirsal-green'
                              : ''
                          }`} />
                          <span>Attendance</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop Tabs (visible on >= 768px) */}
            <div className={`hidden md:block border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <nav className="flex overflow-x-auto scrollbar-hide justify-center sm:justify-center md:justify-stretch -mx-1 px-1">
                <button
                  onClick={() => updateActiveTab('details')}
                  className={`flex-1 md:flex-1 flex-shrink-0 py-3 px-3 sm:py-4 sm:px-4 md:px-6 text-xs sm:text-sm font-semibold transition-all duration-200 whitespace-nowrap min-w-[100px] sm:min-w-[140px] md:min-w-0 ${
                    activeTab === 'details'
                      ? theme === 'dark'
                        ? 'bg-gray-800 text-[#33FF00] border-b-2 border-[#33FF00] -mb-px relative shadow-sm'
                        : 'bg-white text-mirsal-green border-b-2 border-mirsal-green -mb-px relative shadow-sm'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                        : 'text-gray-600 hover:text-mirsal-black hover:bg-gray-200/50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                    <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="hidden min-[375px]:inline">Event Details</span>
                    <span className="min-[375px]:hidden">Details</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    // Only allow guest management for active events
                    if (event && ['active', 'rescheduled'].includes(getEffectiveEventStatus(event))) {
                      updateActiveTab('guests');
                    }
                  }}
                  disabled={event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))}
                  className={`flex-1 md:flex-1 flex-shrink-0 py-3 px-3 sm:py-4 sm:px-4 md:px-6 text-xs sm:text-sm font-semibold transition-all duration-200 whitespace-nowrap min-w-[100px] sm:min-w-[140px] md:min-w-0 ${
                    event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))
                      ? theme === 'dark'
                        ? 'text-gray-500 cursor-not-allowed opacity-50'
                        : 'text-gray-400 cursor-not-allowed opacity-50'
                      : activeTab === 'guests'
                      ? theme === 'dark'
                        ? 'bg-gray-800 text-[#33FF00] border-b-2 border-[#33FF00] -mb-px relative shadow-sm'
                        : 'bg-white text-mirsal-green border-b-2 border-mirsal-green -mb-px relative shadow-sm'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                        : 'text-gray-600 hover:text-mirsal-black hover:bg-gray-200/50'
                  }`}
                  title={
                    event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))
                      ? `Guest management is not available for ${getEffectiveEventStatus(event)} events`
                      : 'Manage event guests'
                  }
                >
                  <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                    <UserCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Guest Management</span>
                    <span className="sm:hidden">Guests</span>
                    {event && getEffectiveEventStatus(event) === 'pending' && (
                      <span className={`ml-1 sm:ml-2 text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 rounded-full hidden md:inline ${
                        theme === 'dark'
                          ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-700/50'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        Approval Required
                      </span>
                    )}
                  </div>
                </button>
                <button
                  onClick={() => {
                    // Only allow attendance management for active events
                    if (event && ['active', 'rescheduled'].includes(getEffectiveEventStatus(event))) {
                      updateActiveTab('attendance');
                    }
                  }}
                  disabled={event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))}
                  className={`flex-1 md:flex-1 flex-shrink-0 py-3 px-3 sm:py-4 sm:px-4 md:px-6 text-xs sm:text-sm font-semibold transition-all duration-200 whitespace-nowrap min-w-[100px] sm:min-w-[140px] md:min-w-0 ${
                    event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))
                      ? theme === 'dark'
                        ? 'text-gray-500 cursor-not-allowed opacity-50'
                        : 'text-gray-400 cursor-not-allowed opacity-50'
                      : activeTab === 'attendance'
                      ? theme === 'dark'
                        ? 'bg-gray-800 text-[#33FF00] border-b-2 border-[#33FF00] -mb-px relative shadow-sm'
                        : 'bg-white text-mirsal-green border-b-2 border-mirsal-green -mb-px relative shadow-sm'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                        : 'text-gray-600 hover:text-mirsal-black hover:bg-gray-200/50'
                  }`}
                  title={
                    event && !['active', 'rescheduled'].includes(getEffectiveEventStatus(event))
                      ? `Attendance management is not available for ${getEffectiveEventStatus(event)} events`
                      : 'Manage guest attendance'
                  }
                >
                  <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                    <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span>Attendance</span>
                  </div>
                </button>
              </nav>
            </div>

          {/* Tab Content */}
          <div className="p-3 sm:p-4 md:p-6">
            {activeTab === 'details' && (
              <div className="space-y-6">
                {/* Enhanced Event Overview Dashboard */}
                <EnhancedOverviewDashboard
                  event={event}
                  guestStats={guestStats}
                  onQuickAction={handleQuickAction}
                />

                {/* Enable Usher Service Banner - Show if service is not enabled */}
                {event && !event.usher_service_enabled && event.can_enable_usher_service && (
                  <div className={`mb-6 p-4 rounded-lg border ${
                    theme === 'dark' 
                      ? 'bg-gray-800 border-gray-700' 
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <UserCog className={`h-5 w-5 ${
                          theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <div>
                          <h3 className={`font-medium ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            Usher Service Not Enabled
                          </h3>
                          <p className={`text-sm mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            Enable usher service to manage ushers and track attendance for this event.
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowEnableUsherServiceConfirm(true)}
                        disabled={!canEditEvent(event) || !event.is_editable}
                      >
                        Enable Usher Service
                      </Button>
                    </div>
                  </div>
                )}

                {/* Action Bar */}
                <EventActionBar
                  isEditing={isEditing}
                  onEdit={async () => {
                    // Refresh event data to get latest can_edit_event value
                    try {
                      const refreshedEvent = await eventAPI.getEvent(event.id);
                      setEvent(refreshedEvent);
                      
                      // Check with refreshed data
                      if (!canEditEvent(refreshedEvent)) {
                        let errorMessage = '';
                        if (!refreshedEvent.can_edit_event) {
                          if (refreshedEvent.editing_window_hours) {
                            errorMessage = `Event editing window has closed. Editing is only allowed ${refreshedEvent.editing_window_hours} hours before the event starts.`;
                          } else {
                            errorMessage = 'Event editing window has closed.';
                          }
                        } else if (hasEventEnded(refreshedEvent)) {
                          errorMessage = 'Event has ended - editing is not allowed.';
                        } else {
                          errorMessage = 'Event editing is not allowed at this time.';
                        }
                        setError(errorMessage);
                        return;
                      }
                      setIsEditing(true);
                    } catch (err) {
                      logError('Failed to refresh event data', err);
                      // Fallback to existing check
                      if (!canEditEvent(event)) {
                        setError('Unable to verify if editing is allowed. Please refresh the page.');
                        return;
                      }
                      setIsEditing(true);
                    }
                  }}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onManageGuests={() => updateActiveTab('guests')}
                  onManageUshers={() => setShowUsherManagement(true)}
                  disabled={!canEditEvent(event) || !event.is_editable || getEffectiveEventStatus(event) === 'rejected' || getEffectiveEventStatus(event) === 'canceled' || getEffectiveEventStatus(event) === 'archived'}
                  editDisabledReason={
                    !canEditEvent(event)
                      ? (!event.can_edit_event 
                          ? (event.editing_window_hours 
                              ? `Event editing window has closed. Editing is only allowed ${event.editing_window_hours} hours before the event starts.`
                              : 'Event editing window has closed.')
                          : hasEventEnded(event)
                          ? 'Event has ended - editing is not allowed.'
                          : 'Event editing is not allowed at this time.')
                      : getEffectiveEventStatus(event) === 'rejected'
                        ? 'Event was rejected - editing is not allowed'
                      : getEffectiveEventStatus(event) === 'canceled'
                      ? 'Event was canceled - editing is not allowed'
                      : getEffectiveEventStatus(event) === 'archived'
                      ? 'Event is archived - editing is not allowed'
                      : !event.is_editable
                      ? 'Event editing is not allowed'
                      : ''
                  }
                  guestManagementDisabled={!['active', 'rescheduled'].includes(getEffectiveEventStatus(event))}
                  guestManagementReason={
                    getEffectiveEventStatus(event) === 'pending' 
                      ? 'Event is pending approval - guest management will be available once approved'
                      : getEffectiveEventStatus(event) === 'rejected'
                      ? 'Event was rejected - guest management is not available'
                      : getEffectiveEventStatus(event) === 'canceled'
                      ? 'Event was canceled - guest management is not available'
                      : getEffectiveEventStatus(event) === 'archived'
                      ? 'Event is archived - guest management is not available'
                      : `Guest management is not available for ${getEffectiveEventStatus(event)} events`
                  }
                  usherManagementDisabled={!['active', 'rescheduled'].includes(getEffectiveEventStatus(event))}
                  usherManagementReason={
                    getEffectiveEventStatus(event) === 'pending' 
                      ? 'Event is pending approval - usher management will be available once approved'
                      : getEffectiveEventStatus(event) === 'rejected'
                      ? 'Event was rejected - usher management is not available'
                      : getEffectiveEventStatus(event) === 'canceled'
                      ? 'Event was canceled - usher management is not available'
                      : getEffectiveEventStatus(event) === 'archived'
                      ? 'Event is archived - usher management is not available'
                      : `Usher management is not available for ${getEffectiveEventStatus(event)} events`
                  }
                  eventTitle={event.title}
                  eventStatus={getEffectiveEventStatus(event)}
                />

                {/* Editing Window Information */}
                {event.editing_window_hours && (
                  <div className={`rounded-xl p-4 border ${
                    theme === 'dark' 
                      ? event.can_edit_event 
                        ? 'bg-blue-900/20 border-blue-700/50' 
                        : 'bg-orange-900/20 border-orange-700/50'
                      : event.can_edit_event 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-orange-50 border-orange-200'
                  }`}>
                    <div className="flex items-center">
                      <Clock className={`h-5 w-5 mr-3 ${
                        theme === 'dark' 
                          ? event.can_edit_event ? 'text-blue-400' : 'text-orange-400'
                          : event.can_edit_event ? 'text-blue-600' : 'text-orange-600'
                      }`} />
                      <div>
                        <h4 className={`text-sm font-medium ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          Event Editing Window
                        </h4>
                        <p className={`text-xs ${
                          theme === 'dark' 
                            ? event.can_edit_event ? 'text-blue-300' : 'text-orange-300'
                            : event.can_edit_event ? 'text-blue-700' : 'text-orange-700'
                        }`}>
                          {event.can_edit_event 
                            ? `Event can be edited until ${event.editing_window_hours} hours before the event starts`
                            : `Event editing window has closed. Editing was only allowed ${event.editing_window_hours} hours before the event starts`
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Enhanced Event Details Form */}
                <EnhancedEventDetailsForm
                  event={event}
                  isEditing={isEditing}
                  editData={editData}
                  onFieldChange={handleInputChange}
                  errors={fieldErrors}
                  categories={categories}
                />

                {/* Series Timeline - Only show if event is part of a series */}
                {event.series ? (
                  <div className="mt-8">
                    <SeriesEventsSection
                      currentEvent={event}
                      onEventView={(selectedEvent) => {
                        // Navigate to the selected event
                        const eventUrl = generateEventViewUrl(selectedEvent.id, selectedEvent.title, selectedEvent.event_date, user?.role, selectedEvent.slug);
                        navigate(eventUrl);
                      }}
                    />
                  </div>
                ) : null}

                {/* Destructive Actions - At the end of the page */}
                <EventDestructiveActions
                  eventStatus={getEffectiveEventStatus(event)}
                  eventTitle={event.title}
                  onCancelEvent={handleCancelEvent}
                  onArchiveEvent={handleArchiveEvent}
                  onUnarchiveEvent={handleUnarchiveEvent}
                  disabled={saving}
                />
              </div>
            )}

            {activeTab === 'guests' && event && (
              <GuestManagement 
                eventId={event.id} 
                onGuestStatsUpdate={refreshGuestStats}
              />
            )}

            {activeTab === 'attendance' && event && (
              <EventAttendanceManagement 
                eventId={event.id}
                eventTitle={event.title}
                eventDate={event.event_date}
                eventTime={event.event_time}
                eventLocation={event.location}
              />
            )}

          </div>
        </div>
        </div>
      </div>
      
      {/* Usher Management Modal */}
      {showUsherManagement && event && (
        <UsherManagement
          eventId={event.id}
          eventTitle={event.title}
          usherServiceEnabled={event.usher_service_enabled || false}
          onClose={() => setShowUsherManagement(false)}
        />
      )}
      
      {/* Reschedule Confirmation Dialog */}
      <RescheduleConfirmationDialog
        isOpen={showRescheduleDialog}
        onClose={() => setShowRescheduleDialog(false)}
        onConfirm={handleRescheduleNotificationConfirm}
        guests={eventGuests}
        eventTitle={event?.title || ''}
        newDate={editData.event_date}
        newTime={editData.event_time}
        loading={sendingNotifications}
        theme={theme}
      />

      {/* Enable Usher Service Confirmation Modal */}
      <ConfirmationModal
        isOpen={showEnableUsherServiceConfirm}
        onClose={() => setShowEnableUsherServiceConfirm(false)}
        onConfirm={handleEnableUsherService}
        title="Enable Usher Service"
        message={`You are about to enable usher service for "${event?.title}".`}
        confirmText="Enable Service"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={enablingUsherService}
      >
        {!usherServiceLoading && usherServicePricing && (
          <BillingActionPreview
            costs={calculateUsherServiceCost()}
            showBalance={true}
            showBalanceAfter={true}
            currentBalance={usherServiceBalance}
            pricing={usherServicePricing}
            showWarning={true}
            compact={true}
            title="Cost Preview"
            infoText="The usher service fee will be deducted from your balance immediately."
          />
        )}
      </ConfirmationModal>
    </div>
  );
};

export default EventViewPage;

export {};